import React from 'react';
import { cn } from '../../utils/cn';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: string | number;
  height?: string | number;
  rounded?: boolean;
  animate?: boolean;
}

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'neutral' | 'white';
}

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  progress: number;
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'success' | 'warning' | 'error';
  animate?: boolean;
  showPercentage?: boolean;
}

export interface LoadingDotsProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'neutral' | 'white';
}

// Skeleton Loading Component
const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ width, height, rounded = true, animate = true, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'bg-neutral-200',
          rounded && 'rounded-lg',
          animate && 'animate-shimmer',
          className,
        )}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
        }}
        {...props}
      />
    );
  },
);

// Spinner Component
const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = 'md', color = 'primary', className, ...props }, ref) => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
    };

    const colorClasses = {
      primary: 'border-primary-500',
      neutral: 'border-neutral-500',
      white: 'border-white',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'animate-spin rounded-full border-2 border-transparent',
          sizeClasses[size],
          colorClasses[color],
          className,
        )}
        style={{
          borderTopColor: 'currentColor',
        }}
        {...props}
      />
    );
  },
);

// Progress Bar Component
const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      progress,
      size = 'md',
      color = 'primary',
      animate = false,
      showPercentage = false,
      className,
      ...props
    },
    ref,
  ) => {
    const sizeClasses = {
      sm: 'h-1',
      md: 'h-2',
      lg: 'h-3',
    };

    const colorClasses = {
      primary: 'bg-primary-500',
      success: 'bg-success-500',
      warning: 'bg-warning-500',
      error: 'bg-error-500',
    };

    return (
      <div className="w-full">
        <div
          ref={ref}
          className={cn(
            'progress-bar w-full bg-neutral-200 rounded-full overflow-hidden',
            sizeClasses[size],
            className,
          )}
          {...props}
        >
          <div
            className={cn(
              'h-full transition-all duration-300 ease-out',
              colorClasses[color],
              animate && 'animate-shimmer',
            )}
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
        {showPercentage && (
          <div className="mt-1 text-xs text-neutral-600 text-right">{Math.round(progress)}%</div>
        )}
      </div>
    );
  },
);

// Loading Dots Component
const LoadingDots = React.forwardRef<HTMLDivElement, LoadingDotsProps>(
  ({ size = 'md', color = 'primary', className, ...props }, ref) => {
    const sizeClasses = {
      sm: 'w-1 h-1',
      md: 'w-2 h-2',
      lg: 'w-3 h-3',
    };

    const colorClasses = {
      primary: 'bg-primary-500',
      neutral: 'bg-neutral-500',
      white: 'bg-white',
    };

    return (
      <div ref={ref} className={cn('flex items-center space-x-1', className)} {...props}>
        <div
          className={cn('rounded-full animate-pulse', sizeClasses[size], colorClasses[color])}
          style={{ animationDelay: '0ms' }}
        />
        <div
          className={cn('rounded-full animate-pulse', sizeClasses[size], colorClasses[color])}
          style={{ animationDelay: '150ms' }}
        />
        <div
          className={cn('rounded-full animate-pulse', sizeClasses[size], colorClasses[color])}
          style={{ animationDelay: '300ms' }}
        />
      </div>
    );
  },
);

// Skeleton Card Component
export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('bg-white rounded-xl p-6 shadow-sm', className)}>
    <div className="flex items-center space-x-4 mb-4">
      <Skeleton width={40} height={40} rounded />
      <div className="flex-1">
        <Skeleton width="60%" height={16} className="mb-2" />
        <Skeleton width="40%" height={12} />
      </div>
    </div>
    <Skeleton width="100%" height={12} className="mb-2" />
    <Skeleton width="80%" height={12} className="mb-2" />
    <Skeleton width="60%" height={12} />
  </div>
);

// Skeleton List Component
export const SkeletonList: React.FC<{ items?: number; className?: string }> = ({
  items = 3,
  className,
}) => (
  <div className={cn('space-y-4', className)}>
    {Array.from({ length: items }).map((_, index) => (
      <div key={index} className="flex items-center space-x-4 p-4">
        <Skeleton width={40} height={40} rounded />
        <div className="flex-1">
          <Skeleton width="70%" height={16} className="mb-2" />
          <Skeleton width="50%" height={12} />
        </div>
        <Skeleton width={60} height={24} rounded />
      </div>
    ))}
  </div>
);

// Skeleton Table Component
export const SkeletonTable: React.FC<{
  rows?: number;
  columns?: number;
  className?: string;
}> = ({ rows = 5, columns = 4, className }) => (
  <div className={cn('bg-white rounded-xl overflow-hidden', className)}>
    <div className="p-6 border-b border-neutral-200">
      <Skeleton width="30%" height={20} />
    </div>
    <div className="p-6">
      <div className="space-y-4">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex space-x-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={colIndex} width={colIndex === 0 ? '40%' : '20%'} height={16} />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

Skeleton.displayName = 'Skeleton';
Spinner.displayName = 'Spinner';
ProgressBar.displayName = 'ProgressBar';
LoadingDots.displayName = 'LoadingDots';

export { Skeleton, Spinner, ProgressBar, LoadingDots };
